import type { Feature, FeatureCollection } from "geojson";
import type { ComponentType } from "react";

import type { DeploymentCompany } from "../../api/types";
import { t } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { useShownDeployment } from "../manoeuvre/useOrientation";
import { revealPaint, useMapRevealed } from "../reveal";
import { FRIEND_BLUE, symbolId } from "./symbols";

/** Carte d'un S/GTIA (niveau compagnie, docs/21 §4, fiche §8) : en rappel discret, le fuseau du GTIA (limites
 *  fines en tirets) et ses autres S/GTIA au premier temps de sa carte (symboles estompés) ; puis les points de
 *  coordination « dans un premier temps, objectifs et base de feux » (OBJ 1 de l'effort, OBJ 2, base de feux), aux
 *  positions que l'ordre cite. Rien ne s'affiche aux autres niveaux. Couches sous le fuseau du S/GTIA et ses unités ;
 *  identifiants « company-… ». La zone, les LIMA, les sections et les symboles de mission sont ceux du dispositif. */

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
}

const FONT = ["Open Sans Semibold"];
const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };
const collection = (features: Feature[]): FeatureCollection => ({ type: "FeatureCollection", features });
/** Rappel discret : bleu ami estompé (le fuseau du S/GTIA garde le trait plein, épaisseur 3). */
const REMINDER_OPACITY = 0.5;
const LABEL_PAINT = { "text-color": "#1f2430", "text-halo-color": "#ffffff", "text-halo-width": 1.4 };

/** Rappel du GTIA : ses deux limites et ses S/GTIA (données GeoJSON). */
export function reminderFeatures(company: DeploymentCompany): { rails: FeatureCollection; neighbours: FeatureCollection } {
  const corridor = company.gtia.corridor;
  const label = t("Fuseau du GTIA {gtia}", { gtia: company.gtia.label });
  const rails = corridor
    ? collection(
        [corridor.upper, corridor.lower].map((coordinates) => ({
          type: "Feature",
          geometry: { type: "LineString", coordinates },
          properties: { label },
        })),
      )
    : EMPTY;
  const neighbours = collection(
    company.gtia.neighbours.map((n) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: n.coordinates },
      properties: { icon: symbolId(n.sidc), label: n.label },
    })),
  );
  return { rails, neighbours };
}

/** Points de coordination : « OBJ 1 », « OBJ 2 » et la base de feux, nommée dans la langue de l'interface. */
export function pointFeatures(company: DeploymentCompany): FeatureCollection {
  return collection(
    company.points.map((p) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: p.coordinates },
      properties: { kind: p.kind, label: p.kind === "fires" ? t("Base de feux") : (p.name ?? "") },
    })),
  );
}

export function CompanyLayers({ Source, Layer }: EngineParts) {
  const { data } = useShownDeployment();
  const revealed = useMapRevealed();
  const control = useUi((s) => s.controlVisible);
  const friends = useUi((s) => s.unitsVisible.AMI);
  const company = data?.company ?? null;
  const shown = revealed && company !== null;
  const reminder = company ? reminderFeatures(company) : { rails: EMPTY, neighbours: EMPTY };
  const points = company ? pointFeatures(company) : EMPTY;
  const railsVisibility = control.fuseau ? "visible" : "none";
  const neighboursVisibility = friends ? "visible" : "none";
  return (
    <>
      <Source id="gx-company-gtia" type="geojson" data={reminder.rails}>
        <Layer
          id="company-gtia-rails"
          type="line"
          layout={{ visibility: railsVisibility, "line-cap": "round" }}
          paint={revealPaint("line", { "line-color": FRIEND_BLUE, "line-width": 1.5, "line-opacity": REMINDER_OPACITY, "line-dasharray": [4, 3] }, shown)}
        />
        <Layer
          id="company-gtia-rails-label"
          type="symbol"
          layout={{
            visibility: railsVisibility,
            "symbol-placement": "line",
            "symbol-spacing": 400,
            "text-field": ["get", "label"],
            "text-font": FONT,
            "text-size": 10,
            "text-optional": true,
          }}
          paint={revealPaint("symbol", { ...LABEL_PAINT, "text-color": FRIEND_BLUE, "text-opacity": 0.8 }, shown)}
        />
      </Source>
      <Source id="gx-company-neighbours" type="geojson" data={reminder.neighbours}>
        <Layer
          id="company-neighbours"
          type="symbol"
          layout={{
            visibility: neighboursVisibility,
            "icon-image": ["get", "icon"],
            "icon-size": 0.75,
            "icon-allow-overlap": true,
            "text-field": ["get", "label"],
            "text-font": FONT,
            "text-size": 10,
            "text-anchor": "top",
            "text-offset": [0, 1.6],
            "text-optional": true,
          }}
          paint={revealPaint("symbol", { ...LABEL_PAINT, "icon-opacity": 0.4, "text-opacity": 0.6 }, shown)}
        />
      </Source>
      <Source id="gx-company-points" type="geojson" data={points}>
        {/* Anneau plus large que le symbole de l'unité qui tient le point (section d'effort, section d'appui) :
            visible aussi quand elle s'y trouve ; nom en biais de l'anneau, à la première place libre (coins
            supérieurs d'abord), jamais sur le symbole, ses désignations ni sa flèche de mission. */}
        <Layer
          id="company-points"
          type="circle"
          layout={{ visibility: railsVisibility }}
          paint={revealPaint(
            "circle",
            {
              "circle-radius": ["match", ["get", "kind"], "objective", 24, 19],
              "circle-color": FRIEND_BLUE,
              "circle-opacity": 0.08,
              "circle-stroke-color": FRIEND_BLUE,
              "circle-stroke-width": 2,
            },
            shown,
          )}
        />
        <Layer
          id="company-points-label"
          type="symbol"
          layout={{
            visibility: railsVisibility,
            "text-field": ["get", "label"],
            "text-font": FONT,
            "text-size": 11,
            "text-variable-anchor": ["bottom-left", "bottom-right", "top-left", "top-right"],
            "text-radial-offset": ["match", ["get", "kind"], "objective", 2.9, 2.6],
            "text-justify": "auto",
            "text-allow-overlap": false,
          }}
          paint={revealPaint("symbol", { ...LABEL_PAINT, "text-color": FRIEND_BLUE }, shown)}
        />
      </Source>
    </>
  );
}
